import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import LogoLight from 'public/images/logo-light.svg?react'
import LogoDark from 'public/images/logo-dark.svg?react'
import LogoIcon from 'public/images/logo-icon.svg?react'

import { ChevronLeft, ChevronRight, Search, X } from 'lucide-react'

import { useTheme } from '@/components/theme-provider'
import { Button, buttonVariants } from '@/components/ui/button'
import SearchBar from '../../../components/SearchBar'
import { usePageInfo } from '../../../config/queries'
import GameSwitcher from './GameSwitcher'
import HeaderDropdown from './HeaderDropdown'
import TeamNavigationMenu from './TeamNavigationMenu'
import EventNavigationMenu from './EventNavigationMenu'
import LoginButtons from '../../../components/LoginButtons'
import CustomDomainHeader from './CustomDomainHeader'
import CustomDomainSidebar from './CustomDomainSidebar'
import { cn } from '@/lib/utils'
import { useCustomDomain } from 'app/frontend/base/hooks/useCustomDomain'
import { useHasSidebar } from 'app/frontend/base/hooks/useHasSidebar'
import { useBreakpoint } from 'app/frontend/base/hooks/useBreakpoint'
import { useActionable } from 'app/frontend/base/hooks/useActionable'

const Header = () => {
  const { customDomain } = useCustomDomain()
  const {
    data: { user },
  } = usePageInfo()

  const [searching, setSearching] = useState(false)
  const hasSidebar = useHasSidebar()
  const breakpoint = useBreakpoint()
  const { t } = useTranslation()
  const { currentScheme } = useTheme()
  const {
    currentAction,
    hasNextAction,
    actionableEntities,
    hasPreviousAction,
    currentActionIndex,
    setCurrentActionIndex,
    dismissActionableEntity,
  } = useActionable(user)

  const Logo = currentScheme === 'dark' ? LogoLight : LogoDark

  return (
    <header
      className={cn(
        'bg-card',
        currentAction ? 'mb-8' : '',
        hasSidebar
          ? 'border-r-2 shrink-0'
          : 'sticky top-0 z-50 w-full border-b',
      )}
    >
      <div
        className={cn(
          'flex justify-between',
          hasSidebar
            ? 'h-screen flex-col sticky top-0 p-4 w-52'
            : 'root-width h-14 items-center',
        )}
      >
        {customDomain ? (
          <>{hasSidebar ? <CustomDomainSidebar /> : <CustomDomainHeader />}</>
        ) : (
          <div className='flex items-center gap-0.5 justify-between'>
            <div className='flex'>
              <Link to='/' className='mr-2'>
                <LogoIcon
                  className='w-10 h-10 hidden max-[420px]:block'
                  color='var(--color-primary)'
                />
                <Logo
                  className='hidden min-[421px]:block h-12 w-[10.7rem]'
                  color='var(--color-primary)'
                />
              </Link>
            </div>

            <div className='mr-4 lg:mr-12 lg:ml-4'>
              <GameSwitcher className='hidden lg:flex' />
            </div>

            <TeamNavigationMenu />
            <EventNavigationMenu />
            {user ? (
              <Link
                to='/profile/plans'
                className='text-sm ml-2 mr-2 hidden min-[500px]:block'
              >
                {t('header.plans')}
              </Link>
            ) : null}
          </div>
        )}

        <div>
          <div
            className={cn('flex items-center', hasSidebar ? '' : 'sm:ml-4 ')}
          >
            {customDomain ? null : (
              <>
                <Link to='/news' className='text-sm mr-6 hidden lg:block'>
                  {t('header.news')}
                </Link>

                <Link to='/support' className='text-sm mr-6 hidden lg:block'>
                  {t('header.support')}
                </Link>

                <Search
                  size={18}
                  className='mr-2 sm:mr-4 cursor-pointer p-2 w-[2.1rem] h-[2.1rem] min-w-[2.1rem] min-h-[2.1rem]'
                  onClick={() => setSearching(true)}
                />

                {searching && (
                  <SearchBar
                    type='dialog'
                    placeholder={t(
                      breakpoint === 'xs'
                        ? 'shared.search'
                        : 'header.search-placeholder',
                    )}
                    setOpen={setSearching}
                  />
                )}
              </>
            )}

            {user ? (
              <HeaderDropdown />
            ) : (
              <LoginButtons inSidebar={hasSidebar} />
            )}
          </div>
        </div>
      </div>

      {currentAction ? (
        <div
          className={cn(
            'fixed w-screen',
            currentAction.className,
            hasSidebar ? 'top-0 z-100' : 'absolute top-14',
          )}
        >
          <div className='px-4 sm:px-8 flex items-center justify-between gap-1 h-8 root-width'>
            <span className='dark:text-muted text-sm font-bold truncate'>
              {currentAction.text}
            </span>
            <div className='flex items-center gap-2 [&>svg]:text-background'>
              {currentAction.onCtaClick ? (
                <Button
                  variant='outline'
                  size='xs'
                  loading={currentAction.ctaLoading}
                  disabled={currentAction.ctaDisabled}
                  onClick={currentAction.onCtaClick}
                >
                  {currentAction.ctaText}
                </Button>
              ) : currentAction.ctaPath ? (
                <Link
                  to={currentAction.ctaPath}
                  className={cn(
                    buttonVariants({ variant: 'outline', size: 'xs' }),
                  )}
                >
                  {currentAction.ctaText}
                </Link>
              ) : null}

              {actionableEntities.length > 1 ? (
                <>
                  <ChevronLeft
                    size={14}
                    className={
                      hasPreviousAction
                        ? 'cursor-pointer'
                        : 'opacity-30 pointer-events-none'
                    }
                    onClick={() =>
                      hasPreviousAction
                        ? setCurrentActionIndex(currentActionIndex - 1)
                        : null
                    }
                  />
                  <ChevronRight
                    size={14}
                    className={
                      hasNextAction
                        ? 'cursor-pointer'
                        : 'opacity-30 pointer-events-none'
                    }
                    onClick={() =>
                      hasNextAction
                        ? setCurrentActionIndex(currentActionIndex + 1)
                        : null
                    }
                  />
                </>
              ) : null}

              {currentAction.dismissible === false ? null : (
                <X
                  size={14}
                  className='cursor-pointer'
                  onClick={() => dismissActionableEntity(currentAction)}
                />
              )}
            </div>
          </div>
        </div>
      ) : null}
    </header>
  )
}

export default Header
